import type { ContenidoAyuda } from "@/ui/AyudaVista";
import { ENFOQUE_TEXTOS, URGENCIA_META } from "@/lib/notifMeta";
import { ATAJOS_EDITOR, botonesEditor } from "./redaccionComun";

/**
 * Ayuda de Mi perfil: una sección por bloque de la página. Antes, el «?» con
 * globito del correo y los tooltips de botones, fichas y encabezados de
 * NotificacionesPanel, MisEquipos, TelegramPanel y BuzonPanel. Los switches y
 * campos explican en línea (bajo el campo); aquí va lo demás.
 *
 * `puedeEnviar` = admin o soporte: solo ellos guardan correo y ven el buzón
 * (el backend se los cierra a «usuario»).
 */
export function ayudaPerfil({ puedeEnviar }: { puedeEnviar: boolean }): ContenidoAyuda {
  return {
    manual: "notificaciones",
    intro: (
      <p>
        Tus datos personales de ArkTickets: el correo desde el que mandas,
        {puedeEnviar ? " la lectura de tu buzón," : ""} por dónde te llegan los avisos y tu
        Telegram. Lo de aquí solo te afecta a ti.
      </p>
    ),
    secciones: [
      {
        titulo: "Mi correo empresarial",
        items: puedeEnviar
          ? [
              {
                label: "Para qué sirve",
                text: "Los correos que mandas desde un caso salen de tu cuenta real (la firma solo se agrega en los que van al proveedor). Sin configurarlo, el botón de enviar correo del caso queda bloqueado.",
              },
              {
                label: "Guardar configuración",
                text: "Pide la contraseña cada vez, aunque solo cambies la firma o la copia oculta. La contraseña se guarda cifrada. Al guardar, la configuración queda «sin verificar» hasta que la pruebes.",
              },
              {
                label: "Probar conexión",
                text: "Sin contraseña escrita, usa lo guardado: entra a tu correo, te manda un correo de prueba a ti mismo y, si llega, marca la configuración como verificada. Con una contraseña escrita en el formulario, solo revisa que el servidor la acepte (no guarda, no manda nada ni marca verificado). Sale gris si no hay contraseña guardada ni escrita.",
              },
              {
                label: "Limpiar configuración",
                text: "Borra tu correo, contraseña, servidor, puerto y firma (te pide confirmar). Para volver a mandar correos tendrás que darlo de alta otra vez.",
              },
              {
                label: "Aviso de arriba",
                text: "Ámbar: no hay correo configurado. Gris: guardado pero sin verificar. Verde: verificado (con la fecha) o la prueba salió bien. Rojo: la prueba falló y dice por qué.",
              },
            ]
          : [
              {
                label: "Tu rol",
                text: "Es de solo consulta: no mandas correos desde los casos, así que no necesitas configurarlo (el sistema no te dejaría guardarlo).",
              },
            ],
      },
      ...(puedeEnviar
        ? [
            { titulo: "Editor de la firma", items: botonesEditor(false) },
            { titulo: "Atajos de teclado (editor de la firma)", items: ATAJOS_EDITOR },
          ]
        : []),
      {
        titulo: "Revisar mi buzón",
        items: puedeEnviar
          ? [
              {
                label: "Probar",
                text: "Intenta entrar a tu buzón una vez, sin tocar nada, y dice si pudo. Si falla, revisa la contraseña de tu correo empresarial (es la misma).",
              },
              {
                label: "✓ Conectado / Se desconectó / Sin revisar todavía",
                text: "Cómo le fue a la última revisión de tu buzón; si se desconectó, dice por qué.",
              },
              {
                label: "Buzón al N %",
                text: "Qué tan lleno está tu buzón según el servidor. Se pone ámbar del 85 % en adelante. Es solo un dato: nadie te avisa por eso.",
              },
            ]
          : [],
      },
      {
        titulo: "Notificaciones: canales y urgencia",
        items: [
          {
            label: "🔒 Siempre",
            text: "La campanita (Alertas) recibe todo aviso y no se apaga. Los demás canales son además de ella.",
          },
          {
            label: "Ruteo por nivel de urgencia",
            text: `Cada renglón es un grupo de Alertas (${URGENCIA_META.urgente.titulo}, ${URGENCIA_META.normal.titulo}, ${URGENCIA_META.informativa.titulo}); cada columna, un canal: 📱 Push = avisos del navegador en tu pantalla, 💬 Telegram = al chat del bot. Prende o apaga cada cruce: guarda al instante. La columna de Telegram sale gris hasta que vincules Telegram (abajo).`,
          },
          {
            label: "▸ Urgencia por tipo de aviso",
            text: "Para cambiar en qué grupo cae cada tipo de aviso: toca 🔴, 🟡 o ⚪ junto al tipo. Eso cambia en qué grupo lo ves en Alertas y por qué canales te llega.",
          },
          {
            label: "Canal apagado vs. ruteo",
            text: "Si apagas el switch del canal (Navegador o Telegram, arriba), ese canal calla para todo, sin importar lo que diga el ruteo.",
          },
        ],
      },
      {
        titulo: "Este navegador y mis equipos",
        items: [
          {
            label: "Activar en este dispositivo / Reactivar",
            text: "Le pide permiso al navegador para mostrar avisos y da de alta este equipo. Si el navegador los tiene bloqueados, se desbloquean desde el candado junto a la dirección del sitio.",
          },
          {
            label: "Enviar prueba a mis dispositivos",
            text: "Manda un aviso de prueba a todos tus equipos dados de alta, aunque tengas el navegador silenciado. Los que ya no existen se dan de baja solos.",
          },
          {
            label: "permiso: permitido / bloqueado / sin pedir",
            text: "Lo que este navegador le tiene permitido al sitio para mostrar avisos.",
          },
          {
            label: "silenciado",
            text: "Tienes apagado el canal Navegador: tus equipos siguen dados de alta, pero no les llega nada.",
          },
          {
            label: "este dispositivo: suscrito / sin suscribir",
            text: "Si este equipo está dado de alta para recibir avisos.",
          },
          {
            label: "Mis equipos: Probar",
            text: "Manda un aviso de prueba solo a ese equipo. Si el navegador lo rechaza, se da de baja solo.",
          },
          {
            label: "Mis equipos: Quitar",
            text: "Da de baja ese equipo y deja de recibir avisos. Ojo: si en ese equipo vuelves a abrir ArkTickets con el permiso de avisos dado, se da de alta solo otra vez; para que no regrese, bloquea los avisos del sitio en ese navegador.",
          },
          {
            label: "este equipo / confirmado / sin confirmar",
            text: "«Este equipo» es en el que estás. Cada equipo se confirma solo cuando abres ArkTickets en él (con el permiso de avisos dado); «sin confirmar» lleva 14 días o más sin confirmarse y suele ser un navegador que ya no escucha.",
          },
        ],
      },
      {
        titulo: "Horario de silencio y modo enfoque",
        items: [
          {
            label: "Horario de silencio",
            text: "Todos los días, entre esas dos horas no suenan el navegador ni Telegram; todo queda en la campanita y al terminar te llega un resumen. Tus recordatorios sí suenan.",
          },
          {
            label: "Fines de semana y festivos",
            text: "Lo mismo, pero sábados, domingos y días festivos de México completos. El resumen llega el primer día hábil.",
          },
          {
            label: "🎯 Modo enfoque (30 min / 1 hora / 2 horas)",
            text: `${ENFOQUE_TEXTOS.titulo}. ${ENFOQUE_TEXTOS.detalle} Es el mismo 🎯 de la barra de arriba; cuando está activo, ahí se ve la hora en que termina.`,
          },
          { label: "Terminar", text: "Apaga el modo enfoque antes de tiempo." },
        ],
      },
      {
        titulo: "Telegram",
        items: [
          {
            label: "Vincular Telegram",
            text: "Genera un enlace de un solo uso que vence en unos minutos. Al abrirlo y tocar INICIAR en el chat del bot, tu cuenta queda vinculada; la pantalla se actualiza sola.",
          },
          {
            label: "Desvincular",
            text: "Deja de mandarte avisos y recordatorios por Telegram y apaga ese canal (te pide confirmar). Puedes volver a vincular cuando quieras.",
          },
          {
            label: "Después de vincular",
            text: "El canal Telegram de Notificaciones se prende solo y ya te llegan avisos al chat. Si quieres pausarlos sin desvincular, apaga ese switch.",
          },
        ],
      },
    ],
  };
}
